import ExtraCard from './ExtraCard';
import Icon from './Icon';
import './EtapeExtras.css';

const ALLERGIES_LISTE = [
  'Gluten', 'Noix', 'Produits laitiers', 'Soja', 'Oeufs', 'Poisson',
];

export default function EtapeExtras({
  extras,             // [{ id, nom, description, prix, unite, categorie, emoji }]
  selectionExtras,    // { [extra_id]: quantite }
  onChangerExtra,     // (extra_id, quantite) => void
  allergies,          // string[]
  onToggleAllergie,   // (allergie) => void
  notesAllergies,
  onNotesAllergies,
  nbPersonnes,        // nombre de personnes total (pour les boissons)
  boissonsOptions,    // [string] liste des noms de boissons disponibles
  boissonsChoisies,   // { [idx]: string } — personne 1-based
  onBoissonChoisie,   // (idx, nomBoisson) => void
  erreur,
}) {
  const extrasByCategorie = {
    extra_viande: (extras || []).filter(e => e.categorie === 'extra_viande'),
    sauce:        (extras || []).filter(e => e.categorie === 'sauce'),
    boisson:      (extras || []).filter(e => e.categorie === 'boisson'),
  };

  const hasVol              = extrasByCategorie.extra_viande.length > 0;
  const hasSauce            = extrasByCategorie.sauce.length > 0;
  const hasBoisson          = extrasByCategorie.boisson.length > 0;
  const hasBoissonsIncluses = nbPersonnes > 0 && boissonsOptions && boissonsOptions.length > 0;

  const personnesArr = hasBoissonsIncluses
    ? Array.from({ length: nbPersonnes }, (_, i) => i + 1)
    : [];

  return (
    <div className="extras-section">
      <h2 className="etape-titre" style={{ justifyContent: 'center', marginBottom: 6 }}>
        <span className="titre-deco">&#10022;</span>
        Envie de plus&nbsp;?
        <span className="titre-deco">&#10022;</span>
      </h2>
      <p className="etape-sous-titre">Ajoutez des extras à votre commande — optionnel</p>

      {hasVol && (
        <div className="extras-categorie">
          <h3 className="extras-cat-label">
            <Icon name="fumage" size={14} weight="bold" color="currentColor" />
            Extras BBQ
          </h3>
          <div className="extras-grid">
            {extrasByCategorie.extra_viande.map(extra => (
              <ExtraCard
                key={extra.id}
                extra={extra}
                quantite={selectionExtras[extra.id] || 0}
                onChanger={qte => onChangerExtra(extra.id, qte)}
              />
            ))}
          </div>
        </div>
      )}

      {hasSauce && (
        <div className="extras-categorie">
          <h3 className="extras-cat-label">
            <Icon name="sauce" size={14} weight="bold" color="currentColor" />
            Sauces
          </h3>
          <div className="extras-grid">
            {extrasByCategorie.sauce.map(extra => (
              <ExtraCard
                key={extra.id}
                extra={extra}
                quantite={selectionExtras[extra.id] || 0}
                onChanger={qte => onChangerExtra(extra.id, qte)}
              />
            ))}
          </div>
        </div>
      )}

      {hasBoisson && (
        <div className="extras-categorie">
          <h3 className="extras-cat-label">
            <Icon name="panier" size={14} weight="bold" color="currentColor" />
            Boissons supplémentaires
          </h3>
          <div className="extras-grid">
            {extrasByCategorie.boisson.map(extra => (
              <ExtraCard
                key={extra.id}
                extra={extra}
                quantite={selectionExtras[extra.id] || 0}
                onChanger={qte => onChangerExtra(extra.id, qte)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── Boissons incluses ─────────────────── */}
      {hasBoissonsIncluses && (
        <div className="boissons-section">
          <h3 className="boissons-titre">
            <Icon name="check" size={14} weight="bold" color="currentColor" />
            Vos boissons incluses
          </h3>
          <p className="boissons-incluses-label">
            <span>{nbPersonnes} boisson{nbPersonnes > 1 ? 's' : ''} incluse{nbPersonnes > 1 ? 's' : ''}</span>
            {' '}— 1 par personne, sans frais supplémentaires
          </p>
          {personnesArr.map(idx => (
            <div key={idx} className="boisson-ligne">
              <span className="boisson-personne-label">Personne {idx}&nbsp;:</span>
              <select
                className="boisson-select"
                value={boissonsChoisies[idx] || ''}
                onChange={e => onBoissonChoisie(idx, e.target.value)}
              >
                <option value="">Choisir une boisson</option>
                {boissonsOptions.map(nom => (
                  <option key={nom} value={nom}>{nom}</option>
                ))}
              </select>
              <span className="boisson-badge-inclus">inclus</span>
            </div>
          ))}
        </div>
      )}

      {/* ─── Allergies ─────────────────────────── */}
      <div className="allergies-section">
        <h3 className="extras-cat-label">
          <Icon name="attention" size={14} weight="bold" color="currentColor" />
          Allergies &amp; restrictions
        </h3>

        <div className="allergies-avertissement">
          <div className="allergie-avert-item">
            <Icon name="saumon" size={13} weight="regular" color="currentColor" />
            <span>
              <strong>Poisson</strong> — Notre sauce BBQ Miel &amp; Bourbon contient de la sauce
              Worcestershire qui peut contenir des traces de poisson (anchois).
            </span>
          </div>
          <div className="allergie-avert-item">
            <Icon name="sauce" size={13} weight="regular" color="currentColor" />
            <span>
              <strong>Soya</strong> — Notre sauce BBQ contient de la sauce soya.
            </span>
          </div>
          <div className="allergie-avert-item">
            <Icon name="laitier" size={13} weight="regular" color="currentColor" />
            <span>
              <strong>Produits laitiers</strong> — Nos saucisses Jalapeño Cheddar contiennent du fromage cheddar.
            </span>
          </div>
        </div>

        <div className="allergies-grid">
          {ALLERGIES_LISTE.map(allergie => (
            <label
              key={allergie}
              className={`allergie-chip${allergies.includes(allergie) ? ' allergie-chip-active' : ''}`}
            >
              <input
                type="checkbox"
                checked={allergies.includes(allergie)}
                onChange={() => onToggleAllergie(allergie)}
                style={{ display: 'none' }}
              />
              <span>{allergie}</span>
            </label>
          ))}
        </div>
        <textarea
          className="allergies-notes"
          placeholder="Précisez vos allergies ou restrictions alimentaires..."
          value={notesAllergies}
          onChange={e => onNotesAllergies(e.target.value)}
        />
      </div>

      {erreur && (
        <div className="etape-erreur">
          <Icon name="erreur" size={15} weight="fill" color="currentColor" />
          {erreur}
        </div>
      )}
    </div>
  );
}
